import cn from 'classnames';
import { useRouter } from 'next/router';
import {
  RatingDataType,
  UserReviewType,
} from 'src/modules/newCars/types/model';
import { ProgressIndicator, Typography } from 'ui';
import FullStar from 'ui/components/StarRating/FullStar';
import { Color, TypeScales } from 'ui/primitives';
import { IndicatorVariants } from 'ui/primitives/ProgressIndicatorTypes';
import { ReviewCard } from '../../../cards/ReviewCard/ReviewCard';
import s from './UserReview.module.scss';

export const UserReview = ({
  ratingData,
  calculatedReview,
  showListing = true,
  link,
}: {
  ratingData: UserReviewType[];
  calculatedReview: RatingDataType;
  showListing?: boolean;
  link: string;
}) => {
  const router = useRouter();
  return (
    <div className={s.root}>
      <Typography
        label={calculatedReview?.title}
        variant={TypeScales.h2}
        renderAs={"H1"}
        className={s.heading}
      />
      <div className={s.box}>
        <div className={s.left}>
          <FullStar height={54} width={54} />
          <div className={s.text}>
            <Typography
              variant={TypeScales.h2}
              label={calculatedReview?.totalRating}
              className={s.rating}
            />
            <Typography
              variant={TypeScales.h5}
              label={`(Based on ${calculatedReview?.ratingCount} users ratings)`}
              className={s.based_on}
            />
          </div>
        </div>
        <div className={s.right}>
          {calculatedReview?.exterior && (
            <div className={s.item}>
              <div className={s.top}>
                <Typography
                  label={calculatedReview.exterior}
                  variant={TypeScales.body_big_m}
                  className={s.circle_rating}
                />
                <ProgressIndicator
                  variant={IndicatorVariants.circular}
                  progress={(calculatedReview.exterior / 5) * 100}
                  color={Color.warning_30}
                  backgroundColor={Color.grey_30}
                  width={3}
                  size={40}
                />
              </div>
              <Typography label="Exterior" variant={TypeScales.body_para_m} />
            </div>
          )}
          {calculatedReview?.comfort && (
            <div className={s.item}>
              <div className={s.top}>
                <Typography
                  label={calculatedReview.comfort}
                  variant={TypeScales.body_big_m}
                  className={s.circle_rating}
                />
                <ProgressIndicator
                  variant={IndicatorVariants.circular}
                  progress={(calculatedReview.comfort / 5) * 100}
                  color={Color.warning_30}
                  backgroundColor={Color.grey_30}
                  width={3}
                  size={40}
                />
              </div>
              <Typography label="Comfort" variant={TypeScales.body_para_m} />
            </div>
          )}
          {calculatedReview?.performance && (
            <div className={s.item}>
              <div className={s.top}>
                <Typography
                  label={calculatedReview.performance}
                  variant={TypeScales.body_big_m}
                  className={s.circle_rating}
                />
                <ProgressIndicator
                  variant={IndicatorVariants.circular}
                  progress={(calculatedReview.performance / 5) * 100}
                  color={Color.warning_30}
                  backgroundColor={Color.grey_30}
                  width={3}
                  size={40}
                />
              </div>
              <Typography
                label="Performance"
                variant={TypeScales.body_para_m}
              />
            </div>
          )}
          {calculatedReview?.mileage && (
            <div className={s.item}>
              <div className={s.top}>
                <Typography
                  label={calculatedReview.mileage}
                  variant={TypeScales.body_big_m}
                  className={s.circle_rating}
                />
                <ProgressIndicator
                  variant={IndicatorVariants.circular}
                  progress={(calculatedReview.mileage / 5) * 100}
                  color={Color.warning_30}
                  backgroundColor={Color.grey_30}
                  width={3}
                  size={40}
                />
              </div>
              <Typography label="Mileage" variant={TypeScales.body_para_m} />
            </div>
          )}
          {calculatedReview?.interior && (
            <div className={s.item}>
              <div className={s.top}>
                <Typography
                  label={calculatedReview.interior}
                  variant={TypeScales.body_big_m}
                  className={s.circle_rating}
                />
                <ProgressIndicator
                  variant={IndicatorVariants.circular}
                  progress={(calculatedReview.interior / 5) * 100}
                  color={Color.warning_30}
                  backgroundColor={Color.grey_30}
                  width={3}
                  size={40}
                />
              </div>
              <Typography label="Interior" variant={TypeScales.body_para_m} />
            </div>
          )}
          {calculatedReview?.looks && (
            <div className={s.item}>
              <div className={s.top}>
                <Typography
                  label={calculatedReview.looks}
                  variant={TypeScales.body_big_m}
                  className={s.circle_rating}
                />
                <ProgressIndicator
                  variant={IndicatorVariants.circular}
                  progress={(calculatedReview.looks / 5) * 100}
                  color={Color.warning_30}
                  backgroundColor={Color.grey_30}
                  width={3}
                  size={40}
                />
              </div>
              <Typography label="Looks" variant={TypeScales.body_para_m} />
            </div>
          )}
          {calculatedReview?.safety && (
            <div className={s.item}>
              <div className={s.top}>
                <Typography
                  label={calculatedReview.safety}
                  variant={TypeScales.body_big_m}
                  className={s.circle_rating}
                />
                <ProgressIndicator
                  variant={IndicatorVariants.circular}
                  progress={(calculatedReview.safety / 5) * 100}
                  color={Color.warning_30}
                  backgroundColor={Color.grey_30}
                  width={3}
                  size={40}
                />
              </div>
              <Typography label="Safety" variant={TypeScales.body_para_m} />
            </div>
          )}
        </div>
      </div>
      <div className={s.divider} />
      {showListing && (
        <div className={s.slider}>
          <div className={s.top_bar}>
            <div className={s.left}>
              <Typography
                label="Park+ verified review"
                variant={TypeScales.h2}
              />
              <div className={s.customer_count}>
                <Typography
                  label="30000+ "
                  variant={TypeScales.h5}
                  color={Color.purple_60}
                  renderAs="SPAN"
                />
                <Typography
                  label="happy customers and counting!"
                  variant={TypeScales.h5}
                  renderAs="SPAN"
                />
              </div>
            </div>
            <button className={cn(s.view_btn, 'cmn_touchable_area')} onClick={() => router.push(link)}>
              <a href={link}>View all</a>
            </button>
          </div>
          <div className={s.bottom_list}>
            {ratingData?.filter((obj, idx) => idx < 3)?.map((obj) => (
              <ReviewCard
                title={obj?.title}
                rating={obj?.totalRating}
                description={obj?.description}
                name={obj?.name}
                date={obj?.date || obj?.publishedAt}
                key={obj?.title}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
